import { Component, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AppState } from 'src/app/redux/app.state';
import { NotificationService } from 'src/app/services/notification.service';
import { SupportService } from 'src/app/support.service';
import {
  UnifiedProductMovementDirection,
  UnifiedProductMovementEventType,
  UnifiedProductMovementItem,
  UnifiedProductMovementSource,
  UnifiedProductMovementWarehouse,
  User
} from 'src/typings';
import { ProductService } from '../product.service';

type WarehouseTabKey = string;

interface WarehouseTab {
  key: WarehouseTabKey;
  label: string;
  count: number;
  current: boolean;
}

@Component({
  selector: 'app-move',
  templateUrl: './move.component.html',
  styleUrls: ['./move.component.scss']
})
export class MoveComponent implements OnInit, OnDestroy {
  constructor(
    private productService: ProductService,
    private notificationService: NotificationService,
    public supportService: SupportService,
    private store: Store<AppState>,
    @Optional() public dialogRef: MatDialogRef<MoveComponent>
  ) {
    this.storeSubscription = this.store.subscribe((data) => {
      this.user = data.user.info;
      this.updateMovementView({ keepSelection: true });
    });
  }

  @Input() product_id: number | undefined;

  rows: UnifiedProductMovementItem[] = [];

  total = 0;

  selectedWarehouseTabKey: WarehouseTabKey = '';

  selectedWarehouseTabIndex = 0;

  warehouseTabs: WarehouseTab[] = [];

  visibleRows: UnifiedProductMovementItem[] = [];

  user: User | undefined | null;

  private storeSubscription: Subscription;

  private pageSubscription?: Subscription;

  private requestVersion = 0;

  selectedEventTypes: UnifiedProductMovementEventType[] = [];

  loadError = false;

  limit = 50;

  offset = 0;

  loading = false;

  loadingMore = false;

  allLoaded = false;

  readonly stockBalanceHint =
    'Физический остаток на складе после события, включая зарезервированный товар. ' +
    'Для старых событий без достоверных записей складского журнала остаток недоступен.';

  readonly typeLabels: Record<string, string> = {
    IMPORT_IN: 'Покупка',
    IMPORT_OUT: 'Списание импорта',
    SALE_OUT: 'Продажа',
    SALE_RETURN: 'Возврат на склад',
    RETURN_TO_PROVIDER_OUT: 'Возврат поставщику',
    RETURN_TO_PROVIDER_IN: 'Возврат от поставщика',
    MOVE: 'Перемещение',
    MOVE_IN: 'Перемещение',
    MOVE_OUT: 'Перемещение',
    ORDER: 'Заказ',
    BUY: 'Закупка',
    WRITE_OFF_OUT: 'Списание',
    SURPLUS_IN: 'Оприходование',
    REGRADING_OUT: 'Переоценка/пересорт OUT',
    REGRADING_IN: 'Переоценка/пересорт IN',
    INVENTORY_SET: 'Инвентаризация',
    MANUAL_ADJUST: 'Ручная корректировка'
  };

  readonly eventLabels: Record<string, string> = {
    CUSTOMER_ORDER: 'Заказ клиента',
    SUPPLIER_ORDER: 'Заказ поставщику',
    EXPECTED_RECEIPT: 'Ожидается поступление',
    PHYSICAL_RECEIPT: 'Товар физически принят',
    GOODS_RECEIPT: 'Приход оприходован',
    RESERVATION_CREATED: 'Товар зарезервирован',
    RESERVATION_PICKED: 'Резерв передан в сборку',
    RESERVATION_CONSUMED: 'Резерв снят при отгрузке',
    RESERVATION_RELEASED: 'Резерв снят',
    PICKING_STARTED: 'Начата сборка заказа',
    PICKING_COMPLETED: 'Заказ собран',
    SHIPMENT: 'Товар отгружен',
    CUSTOMER_RETURN: 'Возврат от клиента',
    SUPPLIER_RETURN: 'Возврат поставщику',
    TRANSFER: 'Перемещение между складами',
    WRITE_OFF: 'Товар списан',
    SURPLUS: 'Товар оприходован',
    REGRADING: 'Пересорт',
    INVENTORY: 'Инвентаризация',
    ADJUSTMENT: 'Корректировка остатка'
  };

  readonly eventOptions = Object.entries(this.eventLabels).map(([value, label]) => ({
    value: value as UnifiedProductMovementEventType, label
  }));

  readonly statusLabels: Record<string, string> = {
    ACTIVE: 'Активен',
    PICKED: 'Собран',
    CONSUMED: 'Исполнен',
    RELEASE_PENDING: 'Ожидает снятия',
    RELEASED: 'Снят',
    OPEN: 'Открыта',
    IN_PROGRESS: 'В работе',
    COMPLETED: 'Завершена',
    EXPECTED: 'Ожидается',
    RECONCILED: 'Сверена',
    PARTIALLY_RECONCILED: 'Частично сверена',
    REVIEW: 'На проверке',
    CANCELLED: 'Отменён'
  };

  readonly documentLabels: Record<string, string> = {
    import: 'Поступление',
    export: 'Продажа',
    buy: 'Закупка',
    order: 'Заказ',
    moving: 'Перемещение',
    return_to_warehouse: 'Возврат на склад',
    return_to_provider: 'Возврат поставщику',
    shipment_by_invoice: 'Отгрузка',
    receiving: 'Приёмка',
    write_off_products: 'Списание',
    surplus_products: 'Оприходование',
    regrading_products: 'Переоценка',
    stocktaking: 'Инвентаризация'
  };

  readonly documentRoutes: Record<string, string> = {
    import: '/docs/import/update',
    export: '/docs/export/update',
    buy: '/docs/buy/update',
    order: '/orders/update',
    moving: '/docs/moving/update',
    return_to_warehouse: '/docs/return_to_warehouse/update',
    return_to_provider: '/docs/return_to_provider/update',
    shipment_by_invoice: '/docs/shipment_by_invoice/update',
    write_off_products: '/docs/write_off_products/update',
    surplus_products: '/docs/surplus_products/update',
    regrading_products: '/docs/regrading_products/update',
    stocktaking: '/docs/stocktaking/update'
  };

  ngOnInit(): void {
    this.dialogRef?.addPanelClass('product-history-dialog');
    if (!this.product_id) return;
    this.loadPage({ reset: true });
  }

  ngOnDestroy(): void {
    this.storeSubscription.unsubscribe();
    this.pageSubscription?.unsubscribe();
    this.requestVersion += 1;
  }

  loadPage(payload: { reset?: boolean } = {}) {
    if (!this.product_id) return;

    const reset = payload.reset === true;
    if (!reset && (this.loading || this.loadingMore)) return;
    if (reset) this.pageSubscription?.unsubscribe();
    const version = ++this.requestVersion;
    this.loadError = false;
    const requestOffset = reset ? 0 : this.offset;

    if (reset) {
      this.rows = [];
      this.total = 0;
      this.offset = 0;
      this.allLoaded = false;
      this.visibleRows = [];
      this.loadingMore = false;
      this.loading = true;
    } else {
      if (this.allLoaded) return;
      this.loadingMore = true;
    }

    this.pageSubscription = this.productService
      .getUnifiedMove(this.product_id, {
        limit: this.limit, offset: requestOffset,
        ...(this.selectedEventTypes.length ? { eventTypes: [...this.selectedEventTypes] } : {})
      })
      .subscribe({
        next: (response) => {
          if (version !== this.requestVersion) return;
          this.rows = reset ? response.data : [...this.rows, ...response.data];
          this.total = Number(response.length) || 0;
          this.offset = requestOffset + response.data.length;
          this.allLoaded = this.rows.length >= this.total || response.data.length === 0;
          this.updateMovementView({ keepSelection: true });
          this.loading = false;
          this.loadingMore = false;
        },
        error: () => {
          if (version !== this.requestVersion) return;
          this.loadError = true;
          this.loading = false;
          this.loadingMore = false;
          this.notificationService.warning('Не удалось загрузить историю движения товара');
        }
      });
  }

  selectEventTypes(eventTypes: UnifiedProductMovementEventType[]) {
    this.selectedEventTypes = [...eventTypes];
    this.loadPage({ reset: true });
  }

  getSupplierLabel(row: UnifiedProductMovementItem): string | null {
    return ['SUPPLIER_ORDER', 'EXPECTED_RECEIPT', 'PHYSICAL_RECEIPT', 'GOODS_RECEIPT', 'SUPPLIER_RETURN'].includes(row.eventType)
      ? row.counterparty?.trim() || null : null;
  }

  loadMore() {
    this.loadPage();
  }

  selectWarehouseTab(index: number) {
    const tab = this.warehouseTabs[index];
    if (!tab) return;

    this.selectedWarehouseTabKey = tab.key;
    this.updateMovementView({ keepSelection: true });
  }

  private buildWarehouseTabs(): WarehouseTab[] {
    if (!this.rows.length) return [];

    const warehouses = new Map<number, { label: string; count: number }>();
    let unknownCount = 0;

    this.rows.forEach((row) => {
      const rowWarehouses = this.getRowWarehouses(row);

      if (!rowWarehouses.length) {
        unknownCount += 1;
        return;
      }

      rowWarehouses.forEach((warehouse) => {
        const existing = warehouses.get(warehouse.id);
        if (existing) {
          existing.count += 1;
        } else {
          warehouses.set(warehouse.id, {
            label: warehouse.name || `Склад #${warehouse.id}`,
            count: 1
          });
        }
      });
    });

    const currentWarehouseId = this.user?.defaultWarehouse?.id;
    const warehouseTabs = Array.from(warehouses.entries())
      .map(([id, warehouse]) => ({
        key: this.getWarehouseTabKey(id),
        label: warehouse.label,
        count: warehouse.count,
        current: id === currentWarehouseId
      }))
      .sort((a, b) => {
        if (a.current !== b.current) return a.current ? -1 : 1;
        return a.label.localeCompare(b.label);
      });

    if (unknownCount > 0) {
      warehouseTabs.push({
        key: 'unknown',
        label: 'Без склада',
        count: unknownCount,
        current: false
      });
    }

    warehouseTabs.push({
      key: 'all',
      label: 'Все склады',
      count: this.rows.length,
      current: false
    });

    return warehouseTabs;
  }

  private getVisibleRows() {
    if (this.selectedWarehouseTabKey === 'all') return this.rows;
    if (this.selectedWarehouseTabKey === 'unknown') {
      return this.rows.filter((row) => this.getRowWarehouses(row).length === 0);
    }

    const selectedWarehouseId = this.getWarehouseIdFromTabKey(this.selectedWarehouseTabKey);
    if (!selectedWarehouseId) return this.rows;

    return this.rows.filter((row) =>
      this.getRowWarehouses(row).some((warehouse) => warehouse.id === selectedWarehouseId)
    );
  }

  getDirectionLabel(direction: UnifiedProductMovementDirection) {
    switch (direction) {
      case 'IN':
        return 'Приход';
      case 'OUT':
        return 'Расход';
      case 'MOVE':
        return 'Перемещение';
      case 'INFO':
        return 'Событие';
      default:
        return direction;
    }
  }

  getTypeLabel(row: UnifiedProductMovementItem) {
    return this.typeLabels[row.type] || row.type || row.documentType;
  }

  getEventLabel(row: UnifiedProductMovementItem) {
    const eventType = row.eventType;
    if (!eventType) return this.getTypeLabel(row);
    if (eventType === 'SUPPLIER_ORDER' && ['Создано', 'Нужно заказать'].includes(row.status || '')) {
      return 'Создан черновик заказа поставщику';
    }
    if (eventType === 'SHIPMENT' && row.direction === 'IN') {
      return 'Отгрузка отменена';
    }
    return this.eventLabels[eventType] || this.getTypeLabel(row);
  }

  getEventIcon(row: UnifiedProductMovementItem) {
    const icons: Record<string, string> = {
      CUSTOMER_ORDER: 'person',
      SUPPLIER_ORDER: 'local_shipping',
      EXPECTED_RECEIPT: 'schedule',
      PHYSICAL_RECEIPT: 'inventory_2',
      GOODS_RECEIPT: 'assignment_turned_in',
      RESERVATION_CREATED: 'lock',
      RESERVATION_PICKED: 'shopping_basket',
      RESERVATION_CONSUMED: 'lock_open',
      RESERVATION_RELEASED: 'lock_open',
      PICKING_STARTED: 'playlist_add_check',
      PICKING_COMPLETED: 'done_all',
      SHIPMENT: 'outbox',
      CUSTOMER_RETURN: 'keyboard_return',
      SUPPLIER_RETURN: 'assignment_return',
      TRANSFER: 'swap_horiz',
      WRITE_OFF: 'delete_outline',
      SURPLUS: 'add_box',
      REGRADING: 'sync_alt',
      INVENTORY: 'fact_check',
      ADJUSTMENT: 'tune'
    };
    return icons[row.eventType || ''] || 'circle';
  }

  getEventClass(row: UnifiedProductMovementItem) {
    const eventType = row.eventType || '';
    if (
      eventType === 'CUSTOMER_ORDER' ||
      eventType === 'SUPPLIER_ORDER' ||
      eventType === 'EXPECTED_RECEIPT' ||
      eventType.startsWith('RESERVATION') ||
      eventType.startsWith('PICKING')
    ) {
      return 'event-plan';
    }
    if (eventType === 'TRANSFER') return 'event-transfer';
    if (row.direction === 'IN') return 'event-in';
    if (row.direction === 'OUT') return 'event-out';
    return 'event-neutral';
  }

  getDateLabel(row: UnifiedProductMovementItem) {
    return new Date(row.date).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  getTimeLabel(row: UnifiedProductMovementItem) {
    return new Date(row.date).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  getQuantityLabel(row: UnifiedProductMovementItem) {
    const quantity = Number(row.quantity).toLocaleString('ru-RU', {
      maximumFractionDigits: 2
    });
    if (row.direction === 'IN') return `+${quantity}`;
    if (row.direction === 'OUT') return `−${quantity}`;
    return quantity;
  }

  getStockBalanceLabels(row: UnifiedProductMovementItem): string[] {
    const selectedWarehouseId = this.getWarehouseIdFromTabKey(this.selectedWarehouseTabKey);
    const warehouses = this.getRowWarehouses(row).filter(
      warehouse => !selectedWarehouseId || warehouse.id === selectedWarehouseId
    );

    if (!warehouses.length) return ['Остаток: нет данных'];

    return warehouses.map(warehouse => {
      const balance = row.stockBalances?.find(item => item.warehouseId === warehouse.id);
      const quantity = balance?.source === 'recorded' &&
        typeof balance.quantity === 'number' && Number.isFinite(balance.quantity)
        ? `${balance.quantity.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} шт.`
        : 'нет данных';
      const warehouseLabel = this.selectedWarehouseTabKey === 'all'
        ? `${balance?.warehouseName || warehouse.name || `Склад #${warehouse.id}`} · `
        : '';
      return `${warehouseLabel}Остаток: ${quantity}`;
    });
  }

  getStatusLabel(row: UnifiedProductMovementItem) {
    if (!row.status) return null;
    return this.statusLabels[row.status] || row.status;
  }

  getWarehouseLabel(row: UnifiedProductMovementItem) {
    const fromName = row.warehouseFrom?.name || '-';
    const toName = row.warehouseTo?.name || '-';

    if (row.direction === 'MOVE') {
      if (!row.warehouseFrom?.name && !row.warehouseTo?.name) return '-';
      return `${fromName} -> ${toName}`;
    }

    if (row.direction === 'IN') {
      return row.warehouseTo?.name || '-';
    }

    if (row.direction === 'OUT') {
      return row.warehouseFrom?.name || '-';
    }

    return row.warehouseTo?.name || row.warehouseFrom?.name || '-';
  }

  getDocumentLabel(row: UnifiedProductMovementItem) {
    if (!row.documentType && !row.documentId) return '-';
    const documentName =
      this.documentLabels[row.documentType] ||
      this.typeLabels[row.type] ||
      row.documentType ||
      'Документ';
    return row.documentId ? `${documentName} #${row.documentId}` : documentName;
  }

  getDocumentLink(row: UnifiedProductMovementItem) {
    const route = this.documentRoutes[row.documentType];
    if (!route || !row.documentId) return null;
    return [route, row.documentId];
  }

  getManagerLabel(row: UnifiedProductMovementItem) {
    return row.user?.name || '-';
  }

  getCounterpartyLabel(row: UnifiedProductMovementItem) {
    return row.counterparty?.trim() || '-';
  }

  getPriceLabel(row: UnifiedProductMovementItem) {
    if (row.price === null || row.price === undefined) return '-';
    return this.supportService.formatMoney(row.price);
  }

  getSourceLabel(source: UnifiedProductMovementSource) {
    return source === 'stock' ? 'Склад' : 'Старый учет';
  }

  getRowClass(row: UnifiedProductMovementItem) {
    return this.getEventClass(row);
  }

  private updateMovementView(payload: { keepSelection?: boolean } = {}) {
    const previousTab = this.warehouseTabs.find(tab => tab.key === this.selectedWarehouseTabKey);
    this.warehouseTabs = this.buildWarehouseTabs();
    if (payload.keepSelection && previousTab && !this.warehouseTabs.some(tab => tab.key === previousTab.key)) {
      this.warehouseTabs.unshift({ ...previousTab, count: 0 });
    }

    if (!this.warehouseTabs.length) {
      this.selectedWarehouseTabKey = '';
      this.selectedWarehouseTabIndex = 0;
      this.visibleRows = [];
      return;
    }

    const selectedTabExists = this.warehouseTabs.some(
      (tab) => tab.key === this.selectedWarehouseTabKey
    );

    if (!payload.keepSelection || !selectedTabExists) {
      this.selectedWarehouseTabKey = this.getPreferredWarehouseTabKey();
    }

    this.selectedWarehouseTabIndex = Math.max(
      this.warehouseTabs.findIndex((tab) => tab.key === this.selectedWarehouseTabKey),
      0
    );
    this.visibleRows = this.getVisibleRows();
  }

  private getPreferredWarehouseTabKey() {
    const currentWarehouseId = this.user?.defaultWarehouse?.id;
    const currentWarehouseTab = currentWarehouseId
      ? this.warehouseTabs.find((tab) => tab.key === this.getWarehouseTabKey(currentWarehouseId))
      : undefined;

    return currentWarehouseTab?.key || this.warehouseTabs[0].key;
  }

  private getRowWarehouses(row: UnifiedProductMovementItem) {
    const warehouses: UnifiedProductMovementWarehouse[] = [];
    const seenWarehouseIds = new Set<number>();

    [row.warehouseFrom, row.warehouseTo].forEach((warehouse) => {
      if (!warehouse?.id || seenWarehouseIds.has(warehouse.id)) return;
      seenWarehouseIds.add(warehouse.id);
      warehouses.push(warehouse);
    });

    return warehouses;
  }

  private getWarehouseTabKey(warehouseId: number) {
    return `warehouse:${warehouseId}`;
  }

  private getWarehouseIdFromTabKey(key: WarehouseTabKey) {
    const id = Number(key.replace('warehouse:', ''));
    return Number.isFinite(id) ? id : null;
  }
}
